import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import { useRecords } from "@/hooks/useRecords";
import { totalTarget, mealTargets } from "@/mocks/records";
import { foodCategories, categoryColors } from "@/mocks/fooddb";
import { weeklyTrend } from "@/mocks/analysis";
import { targets } from "@/mocks/recognition";

const mealLabels: Record<string, { label: string; icon: string }> = {
  breakfast: { label: "早餐", icon: "ri-sun-line" },
  lunch: { label: "午餐", icon: "ri-restaurant-line" },
  dinner: { label: "晚餐", icon: "ri-moon-clear-line" },
};

function MacroBar({ label, value, max, unit, color }: {
  label: string; value: number; max: number; unit: string; color: string;
}) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-xs font-medium text-gray-700">
          {Math.round(value)} / {max} {unit}
        </span>
      </div>
      <div className="h-2.5 bg-warm-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }}></div>
      </div>
    </div>
  );
}

export default function AnalysisPage() {
  const navigate = useNavigate();
  const records = useRecords();

  const stats = useMemo(() => {
    const total = {
      calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, vitC: 0,
    };
    const meal: Record<string, number> = { breakfast: 0, lunch: 0, dinner: 0 };
    const cat: Record<string, number> = {};
    records.forEach((r) => {
      total.calories += r.calories;
      total.protein += r.protein;
      total.carbs += r.carbs;
      total.fat += r.fat;
      total.fiber += r.fiber;
      total.vitC += r.vitC;
      meal[r.mealType] = (meal[r.mealType] ?? 0) + r.calories;
      cat[r.category] = (cat[r.category] ?? 0) + r.calories;
    });

    // 营养评分：热量接近目标 + 三大营养素均衡
    let score = 100;
    score -= Math.abs(total.calories / totalTarget - 1) * 40;
    if (total.protein < targets.protein) score -= (targets.protein - total.protein) * 0.4;
    if (total.fat > targets.fat) score -= (total.fat - targets.fat) * 0.5;
    score = Math.max(0, Math.min(100, Math.round(score)));

    return { total, meal, cat, score };
  }, [records]);

  const { total, meal, cat, score } = stats;

  const rating =
    score >= 85 ? "优秀" : score >= 70 ? "良好" : score >= 50 ? "一般" : "需调整";

  const advice: string[] = [];
  if (total.calories < totalTarget * 0.8) advice.push("今日总热量偏低，建议下一餐适当增加主食或优质蛋白。");
  else if (total.calories > totalTarget * 1.1) advice.push("今日总热量偏高，建议减少高油高糖食物，增加蔬菜摄入。");
  if (total.protein < targets.protein * 0.8) advice.push("蛋白质摄入不足，可补充鸡蛋、鸡胸肉、豆腐或牛奶。");
  if (total.fat > targets.fat) advice.push("脂肪摄入偏高，注意少油烹饪，控制油炸和肥肉。");
  if (total.fiber < targets.fiber * 0.7) advice.push("膳食纤维偏少，多吃蔬菜、水果和全谷物。");
  if (advice.length === 0) advice.push("今日营养摄入均衡，继续保持！");

  const maxTrend = Math.max(...weeklyTrend.map((d) => d.calories), 1);

  const catEntries = foodCategories
    .map((c) => ({ c, v: cat[c] ?? 0 }))
    .filter((x) => x.v > 0);
  const maxCat = Math.max(...catEntries.map((x) => x.v), 1);

  return (
    <AppShell>
      <header className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-warm-200 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-gray-600 text-lg"></i>
        </button>
        <h1 className="text-base font-semibold text-gray-900">营养分析</h1>
      </header>

      {/* Score card */}
      <section className="px-5 mt-1">
        <div className="bg-white rounded-card p-5 border border-warm-200 flex items-center gap-5">
          <div
            className="relative w-24 h-24 shrink-0 rounded-full flex items-center justify-center"
            style={{ background: `conic-gradient(#4ECDC4 ${score * 3.6}deg, #F5F0E8 0deg)` }}
          >
            <div className="absolute inset-2 bg-white rounded-full flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-900">{score}</span>
              <span className="text-[10px] text-gray-400">分</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">今日营养评分 · {rating}</p>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              摄入 {total.calories} kcal，目标 {totalTarget} kcal
            </p>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">达标率</span>
                <span className="font-medium text-gray-700">{Math.round((total.calories / totalTarget) * 100)}%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Macro */}
      <section className="px-5 mt-4">
        <div className="bg-white rounded-card p-4 border border-warm-200">
          <p className="text-sm font-semibold text-gray-900 mb-3">营养素分析</p>
          <div className="space-y-3">
            <MacroBar label="蛋白质" value={total.protein} max={targets.protein} unit="g" color="#10B981" />
            <MacroBar label="碳水" value={total.carbs} max={targets.carbs} unit="g" color="#F59E0B" />
            <MacroBar label="脂肪" value={total.fat} max={targets.fat} unit="g" color="#EF4444" />
            <MacroBar label="膳食纤维" value={total.fiber} max={targets.fiber} unit="g" color="#8B5CF6" />
            <MacroBar label="维生素C" value={total.vitC} max={targets.vitC} unit="mg" color="#F97316" />
          </div>
        </div>
      </section>

      {/* Meal distribution */}
      <section className="px-5 mt-4">
        <div className="bg-white rounded-card p-4 border border-warm-200">
          <p className="text-sm font-semibold text-gray-900 mb-3">三餐分布</p>
          <div className="space-y-3">
            {Object.entries(meal).map(([key, cal]) => {
              const target = mealTargets[key as keyof typeof mealTargets];
              const pct = Math.min(100, Math.round((cal / target) * 100));
              const info = mealLabels[key];
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <i className={info.icon}></i> {info.label}
                    </span>
                    <span className="text-xs text-gray-500">{cal} / {target} kcal</span>
                  </div>
                  <div className="h-2 bg-warm-100 rounded-full overflow-hidden">
                    <div className="h-full bg-mint-400 rounded-full" style={{ width: `${pct}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Category distribution */}
      <section className="px-5 mt-4">
        <div className="bg-white rounded-card p-4 border border-warm-200">
          <p className="text-sm font-semibold text-gray-900 mb-3">食物类别占比</p>
          {catEntries.length === 0 ? (
            <p className="text-xs text-gray-400">暂无数据</p>
          ) : (
            <div className="space-y-3">
              {catEntries.map(({ c, v }) => {
                const pct = Math.round((v / maxCat) * 100);
                return (
                  <div key={c}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${categoryColors[c] ?? categoryColors["其他"]}`}>
                        {c}
                      </span>
                      <span className="text-xs text-gray-500">{v} kcal</span>
                    </div>
                    <div className="h-2 bg-warm-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Weekly trend */}
      <section className="px-5 mt-4">
        <div className="bg-white rounded-card p-4 border border-warm-200">
          <p className="text-sm font-semibold text-gray-900 mb-3">近 7 天热量趋势</p>
          <div className="flex items-end justify-between gap-2 h-32">
            {weeklyTrend.map((d) => {
              const h = Math.round((d.calories / maxTrend) * 100);
              const over = d.calories > totalTarget;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-gray-400">{d.calories}</span>
                  <div className="w-full flex items-end justify-center h-20">
                    <div
                      className={`w-5 rounded-t-md ${over ? "bg-red-300" : "bg-mint-400"}`}
                      style={{ height: `${h}%` }}
                    ></div>
                  </div>
                  <span className="text-[10px] text-gray-500">{d.day}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI advice */}
      <section className="px-5 mt-4 pb-8">
        <div className="rounded-card p-4 bg-mint-50 border border-mint-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-mint-400 text-white">
              <i className="ri-sparkling-2-line"></i>
            </span>
            <p className="text-sm font-medium text-mint-600">AI 营养建议</p>
          </div>
          <ul className="space-y-2">
            {advice.map((a, i) => (
              <li key={i} className="text-xs text-gray-600 leading-relaxed flex gap-2">
                <span className="text-mint-500 shrink-0">·</span>
                {a}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </AppShell>
  );
}